import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, PauseCircle } from 'lucide-react';
import { SubUserLockout } from './SubUserLockout';

/**
 * Renders an interstitial paywall when the authenticated user's 7-day trial
 * has lapsed and they don't hold an active subscription. Wraps any route's
 * children — when `has_access` is true, it's a no-op pass-through.
 *
 * Iter-100 (2026-05-30): for ``access_reason === 'expired'`` (trial + paid
 * cycle both elapsed) we now PASS-THROUGH children so the global
 * RenewSubscriptionDialog + read-only mode can take over. The hard
 * paywall is reserved for soft-deleted / data-purged users only.
 *
 * Iter-106 (2026-05-30): for sub-users (Team Access members) of an
 * EXPIRED admin we render the dedicated `SubUserLockout` interstitial
 * which blocks every page entirely — sub-users have no purchasing
 * authority, so the read-only nag the admin sees doesn't apply to
 * them. They get a Notify-owner CTA + Sign-out path.
 */
/**
 * Iter-234 — Admin-paused interstitial. Reads `user.pause_reason` +
 * `user.paused_at` (surfaced by `_enrich_user_with_access`) and renders
 * a full-screen message. Sign-out is the only action — the user cannot
 * interact with the app until the admin resumes them. The duration-paused
 * live-updates (ticks every minute) so the user sees that time is being
 * accrued and will be credited back on resume.
 */
const PausedInterstitial = ({ user, onLogout }) => {
  const reason = (user?.pause_reason || '').trim();
  const pausedAt = user?.paused_at;
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  // Pretty-print the pause duration ("3 days", "4 hours", "a few minutes").
  let pausedForPretty = '';
  if (pausedAt) {
    try {
      const diffMs = Math.max(0, Date.now() - new Date(pausedAt).getTime());
      const days = Math.floor(diffMs / 86_400_000);
      const hours = Math.floor((diffMs % 86_400_000) / 3_600_000);
      const parts = [];
      if (days) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
      if (hours) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
      pausedForPretty = parts.length ? parts.join(' and ') : 'a few minutes';
    } catch (_) { /* */ }
  }
  // Reference `tick` so React keeps re-rendering as the duration grows.
  void tick;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: '#F7F7F5' }}
      data-testid="paused-interstitial"
    >
      <div
        className="max-w-xl w-full rounded-2xl overflow-hidden"
        style={{ border: '1px solid #E5E5DF', backgroundColor: '#FFFFFF' }}
      >
        <div className="px-8 py-10 text-center" style={{ backgroundColor: '#FEF3C7', color: '#78350F' }}>
          <div
            className="h-12 w-12 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(146, 64, 14, 0.12)' }}
          >
            <PauseCircle className="h-6 w-6" style={{ color: '#92400E' }} />
          </div>
          <p
            className="text-xs uppercase font-medium mb-2"
            style={{ color: '#92400E', letterSpacing: '0.14em' }}
          >
            Access paused
          </p>
          <h1
            className="text-3xl font-medium mb-3"
            style={{ fontFamily: 'Work Sans', letterSpacing: '-0.01em', color: '#78350F' }}
          >
            Your access is temporarily paused
          </h1>
          <p className="text-sm leading-relaxed max-w-md mx-auto" style={{ color: '#92400E' }}>
            An administrator has paused your EquiTrack account. The time you spend paused will be
            credited back to your expiry when it's resumed — you won't lose any days.
          </p>
        </div>

        <div className="px-8 py-8">
          {reason && (
            <div
              className="mb-5 rounded-lg px-4 py-3 text-sm"
              style={{ backgroundColor: '#F7F7F5', borderLeft: '3px solid #92400E', color: '#1C1917' }}
              data-testid="paused-reason"
            >
              <span className="font-medium" style={{ color: '#78350F' }}>Reason: </span>
              {reason}
            </div>
          )}
          {pausedForPretty && (
            <p className="text-sm mb-5" style={{ color: '#57534E' }} data-testid="paused-duration">
              Paused for <strong style={{ color: '#1C1917' }}>{pausedForPretty}</strong> so far —
              these will be added back to your expiry on resume.
            </p>
          )}
          <p className="text-sm mb-6" style={{ color: '#57534E' }}>
            If you believe this is a mistake, please contact support.
          </p>

          <div className="flex items-center justify-between text-xs">
            <Link
              to="/contact"
              className="underline"
              style={{ color: '#1B4332' }}
              data-testid="paused-contact-support"
            >
              Contact support →
            </Link>
            <button
              type="button"
              onClick={onLogout}
              className="underline"
              style={{ color: '#78716C' }}
              data-testid="paused-logout"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PaywallGuard = ({ children }) => {
  const { user, logout } = useAuth();

  if (!user || typeof user !== 'object') return children;
  if (user.has_access) return children;
  // Iter-234 — admin-triggered pause. Full-screen interstitial with the
  // admin-written reason. User can sign out but cannot interact with the
  // app until the admin resumes them.
  if (user.access_reason === 'paused') {
    return <PausedInterstitial user={user} onLogout={logout} />;
  }
  // Iter-106: sub-user of an expired admin → full lockout (no data, no pages).
  if (user.is_sub_user && user.access_reason === 'expired') {
    return <SubUserLockout />;
  }
  // Iter-100: expired admin users get the read-only Layout + nag dialog,
  // NOT the hard paywall. The Layout-level effect fires
  // showRenewDialog() on every route change so the prompt is
  // unavoidable without renewing.
  if (user.access_reason === 'expired') return children;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: '#F7F7F5' }}
      data-testid="paywall-screen"
    >
      <div
        className="max-w-xl w-full rounded-2xl overflow-hidden"
        style={{ border: '1px solid #E5E5DF', backgroundColor: '#FFFFFF' }}
      >
        <div
          className="px-8 py-10 text-center"
          style={{ backgroundColor: '#1B4332', color: '#F7F7F5' }}
        >
          <div
            className="h-12 w-12 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(168, 230, 193, 0.15)' }}
          >
            <Lock className="h-5 w-5" style={{ color: '#A8E6C1' }} />
          </div>
          <p
            className="text-xs uppercase font-medium mb-2"
            style={{ color: '#A9C8B6', letterSpacing: '0.14em' }}
          >
            Trial ended
          </p>
          <h1
            className="text-3xl font-medium mb-3"
            style={{ fontFamily: 'Work Sans', letterSpacing: '-0.01em' }}
          >
            Your free trial has ended
          </h1>
          <p className="text-sm leading-relaxed max-w-sm mx-auto" style={{ color: '#A9C8B6' }}>
            Keep every portfolio, transaction and report you've built. Subscribe to EquiTrack
            Pro to continue where you left off.
          </p>
        </div>

        <div className="px-8 py-8">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="text-3xl font-medium" style={{ color: '#1C1917', fontFamily: 'Work Sans' }}>
                Plans from ₹500
                <span className="text-sm font-normal ml-2" style={{ color: '#57534E' }}>
                  / month / portfolio
                </span>
              </p>
              <p className="text-xs mt-1" style={{ color: '#78716C' }}>
                Pay monthly per portfolio · Upgrade or downgrade anytime
              </p>
            </div>
            <span
              className="text-xs px-2 py-1 rounded"
              style={{ backgroundColor: '#E8F0EC', color: '#1B4332' }}
            >
              Secure · Razorpay
            </span>
          </div>

          <Link
            to="/pricing"
            className="block text-center w-full px-6 py-3 rounded-lg text-sm font-medium"
            style={{ backgroundColor: '#1B4332', color: '#F7F7F5', border: 'none' }}
            data-testid="paywall-subscribe-btn"
          >
            Choose your plan
          </Link>

          <div className="flex items-center justify-between mt-5 text-xs">
            <Link
              to="/pricing"
              className="underline"
              style={{ color: '#57534E' }}
              data-testid="paywall-see-pricing"
            >
              See what's included →
            </Link>
            <button
              type="button"
              onClick={logout}
              className="underline"
              style={{ color: '#78716C' }}
              data-testid="paywall-logout"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallGuard;
